/**
 * Utilidades de importes.
 *
 * Regla del sistema: los importes se calculan con toda la precisión disponible y
 * se redondean a 2 decimales SOLO al cerrar un importe (importe de línea, total de
 * venta, base imponible). Nunca se redondea un valor intermedio.
 *
 * Excepción: las conversiones de divisa se redondean a entero. Ver `obtenerPrecioEuros` /
 * `obtenerPrecioDolares` en `src/utils/exchange.js`.
 */

// Coma decimal y sin ceros sobrantes: 207 / 190,5 / 410,44 (petición del cliente, 2026-10-06)
const nfEs = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Redondea un importe a 2 decimales, corrigiendo el error de representación binaria. */
export const round2 = (value: number | string | null | undefined): number => {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

/**
 * Convierte a número un importe tecleado por el usuario.
 * Acepta la coma decimal española: '57,5' -> 57.5.
 */
export const parseImporte = (value: number | string | null | undefined): number => {
  if (typeof value === 'number') return isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(String(value).trim().replace(',', '.'));
  return isFinite(n) ? n : 0;
};

/** Formatea un importe con hasta 2 decimales y su símbolo de moneda: 57,5 € */
export const formatImporte = (value: number | string | null | undefined, moneda: string = 'EUR'): string =>
  `${nfEs.format(round2(value))} ${moneda === 'EUR' ? '€' : '$'}`;

/** Formatea un importe con hasta 2 decimales, sin símbolo de moneda. */
export const formatImporteSinMoneda = (value: number | string | null | undefined): string => nfEs.format(round2(value));

/**
 * Campos de importe de los formularios de línea de venta y de servicio.
 * Se normalizan en bloque al enviar, para admitir la coma decimal española.
 */
const CAMPOS_IMPORTE = [
  'PrecioUnitarioEuros',
  'InSituLineaDolares',
  'InSituLineaEuros',
  'CosteEuros',
  'CosteDolares',
  'ReservaEuros',
  'ReservaLineaEuros',
  'ReservaLineaDolares',
  'ReventaLineaEuros',
  'ReventaLineaDolares',
  'TasaDolares',
  'TasaEuros',
  'SuplementoTransporteDolares',
  'SuplementoTransporteEuros',
  'PrecioVentaEuros',
  'PorcDescuento',
  // Formularios de servicio de revendedor: usan nombres en minuscula.
  'precioVenta',
  'importeReventa',
];

/**
 * Devuelve los campos de importe que el usuario ha tecleado con coma decimal,
 * ya con el punto sustituido: `{ ReservaLineaEuros: '57.5' }`, o `null` si no hay
 * ninguno.
 *
 * A diferencia de `normalizeImportes`, NO convierte a número: mientras se teclea,
 * '57.' es un estado intermedio legítimo que no debe colapsar a 57.
 * Lo usa `NormalizaImportes` para mantener `values` siempre operable.
 */
export const importesConComa = (values: Record<string, any>): Record<string, string> | null => {
  let cambios: Record<string, string> | null = null;
  CAMPOS_IMPORTE.forEach(campo => {
    const valor = values[campo];
    if (typeof valor === 'string' && valor.includes(',')) {
      if (!cambios) cambios = {};
      cambios[campo] = valor.replace(',', '.');
    }
  });
  return cambios;
};

/**
 * Devuelve una copia de los valores de Formik con los campos de importe
 * convertidos a número (aceptando coma decimal). Los campos ausentes se ignoran.
 */
export const normalizeImportes = (values: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = { ...values };
  CAMPOS_IMPORTE.forEach(campo => {
    if (out[campo] !== undefined && out[campo] !== null && out[campo] !== '') {
      out[campo] = parseImporte(out[campo]);
    }
  });
  return out;
};
